import type {
  ChatConversationView,
  ChatInbox,
  ChatLiveEvent,
  ChatMessageView,
  ChatReportReason,
  ChatSettings,
} from '@mogzo/shared';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { API_URL, apiFetch } from './api';
import { refreshUnread as refreshBell } from './notifications';

export type {
  ChatConversationSummary,
  ChatConversationView,
  ChatInbox,
  ChatLiveEvent,
  ChatMessageView,
  ChatSettings,
} from '@mogzo/shared';

// --- Calls ---------------------------------------------------------------------------------

export const fetchInbox = () => apiFetch<ChatInbox>('/v1/chat');

export function fetchConversation(id: string, before?: string) {
  const query = before ? `?before=${encodeURIComponent(before)}` : '';
  return apiFetch<ChatConversationView>(`/v1/chat/${id}${query}`);
}

const post = <T>(path: string, body?: unknown) =>
  apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) });

export const sendMessage = (id: string, text: string) =>
  post<{ message: ChatMessageView }>(`/v1/chat/${id}/messages`, { text });

/** A message to someone by name: their chat, started as a request if they're new. */
export const messagePlayer = (username: string, text: string) =>
  post<{ conversationId: string; message: ChatMessageView }>('/v1/chat/direct', {
    username,
    text,
  });

export const makeGroup = (name: string, usernames: string[]) =>
  post<{ conversationId: string }>('/v1/chat/groups', { name, usernames });

export const markChatRead = (id: string) => post<{ read: boolean }>(`/v1/chat/${id}/read`);
export const acceptRequest = (id: string) => post(`/v1/chat/${id}/accept`);
export const declineRequest = (id: string) => post(`/v1/chat/${id}/decline`);
export const hideChat = (id: string) => post(`/v1/chat/${id}/hide`);
export const leaveGroup = (id: string) => post(`/v1/chat/${id}/leave`);
export const addToGroup = (id: string, username: string) =>
  post(`/v1/chat/${id}/members`, { username });
export const removeFromGroup = (id: string, userId: string) =>
  apiFetch(`/v1/chat/${id}/members/${userId}`, { method: 'DELETE' });
export const changeChat = (id: string, change: { muted?: boolean; name?: string }) =>
  apiFetch(`/v1/chat/${id}`, { method: 'PATCH', body: JSON.stringify(change) });
export const deleteMessage = (id: string, messageId: string) =>
  apiFetch(`/v1/chat/${id}/messages/${messageId}`, { method: 'DELETE' });

export const reportMessage = (
  messageId: string,
  report: { reason: ChatReportReason; note?: string; block: boolean },
) => post(`/v1/chat/messages/${messageId}/report`, report);

export const blockPlayer = (username: string) => post('/v1/chat/blocks', { username });
export const unblockPlayer = (username: string) =>
  apiFetch(`/v1/chat/blocks/${encodeURIComponent(username)}`, { method: 'DELETE' });

export const fetchChatSettings = () => apiFetch<ChatSettings>('/v1/chat/settings');
export const saveChatSettings = (
  change: Partial<Pick<ChatSettings, 'requests' | 'hideSwearing' | 'readMarks'>>,
) => apiFetch('/v1/chat/settings', { method: 'PATCH', body: JSON.stringify(change) });

export const findPeople = (q: string) =>
  apiFetch<{ people: { username: string }[] }>(`/v1/chat/people?q=${encodeURIComponent(q)}`);

// --- The live connection --------------------------------------------------------------------

type Listener = (event: ChatLiveEvent) => void;
const listeners = new Set<Listener>();
let source: EventSource | null = null;
let fallback: number | null = null;
let users = 0;

/** Whether the live connection is up; while it isn't, the page asks every 30 seconds. */
export const isLive = () => source !== null && source.readyState === 1;
const FALLBACK_MS = 30_000;

function startLive() {
  if (typeof EventSource === 'undefined') return;
  source = new EventSource(`${API_URL}/v1/chat/live`, { withCredentials: true });
  source.onmessage = (message) => {
    let event: ChatLiveEvent;
    try {
      event = JSON.parse(message.data) as ChatLiveEvent;
    } catch {
      return;
    }
    if (event.type === 'bell') refreshBell();
    for (const listener of listeners) listener(event);
  };
  // After a drop, what was missed: everything looks again once it's back.
  source.onopen = () => {
    for (const listener of listeners) listener({ type: 'changed', conversationId: null });
  };
  fallback = window.setInterval(() => {
    if (!isLive() && document.visibilityState === 'visible') {
      for (const listener of listeners) listener({ type: 'changed', conversationId: null });
    }
  }, FALLBACK_MS);
}

function stopLive() {
  source?.close();
  source = null;
  if (fallback !== null) window.clearInterval(fallback);
  fallback = null;
}

/** Calls back with every live event while mounted; one connection for the whole tab. */
export function useChatLive(enabled: boolean, listener: Listener) {
  useEffect(() => {
    if (!enabled) return;
    listeners.add(listener);
    users++;
    if (!source && users === 1) startLive();
    return () => {
      listeners.delete(listener);
      users--;
      if (!users) stopLive();
    };
  }, [enabled, listener]);
}

// --- The messages icon's number -----------------------------------------------------------

let counts: { unread: number; requests: number } | null = null;
const countListeners = new Set<() => void>();
let asking = false;

export function refreshChatCounts() {
  if (asking) return;
  asking = true;
  apiFetch<{ unread: number; requests: number }>('/v1/chat/unread')
    .then((next) => {
      counts = next;
      for (const listener of countListeners) listener();
    })
    .catch(() => undefined)
    .finally(() => {
      asking = false;
    });
}

const countsListener: Listener = (event) => {
  if (event.type !== 'message' || !event.message.mine) refreshChatCounts();
};

/** Chats with something unread and requests waiting; null until known. */
export function useChatCounts(enabled: boolean) {
  const value = useSyncExternalStore(
    (listener) => {
      countListeners.add(listener);
      return () => countListeners.delete(listener);
    },
    () => counts,
  );
  useChatLive(enabled, countsListener);
  useEffect(() => {
    if (enabled && !counts) refreshChatCounts();
  }, [enabled]);
  return enabled ? value : null;
}

// --- The chat window on a computer ----------------------------------------------------------

export interface DockState {
  open: boolean;
  /** The chat open in it; null shows the list. */
  conversationId: string | null;
}

let dock: DockState = { open: false, conversationId: null };
const dockListeners = new Set<() => void>();

export function setDock(next: DockState) {
  dock = next;
  for (const listener of dockListeners) listener();
}

/** The window's state, kept while the player moves between pages. */
export function useDock(): DockState {
  return useSyncExternalStore(
    (listener) => {
      dockListeners.add(listener);
      return () => dockListeners.delete(listener);
    },
    () => dock,
  );
}

/** "14:02" today, "Yesterday", or "Oct 30". */
export function dayLabel(iso: string, now = new Date()): string {
  const date = new Date(iso);
  const days = Math.round(
    (Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) -
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())) /
      86_400_000,
  );
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
}

export const clock = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

/** Re-renders every minute, for "2 min" style times. */
export function useMinute() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setTick((n) => n + 1), 60_000);
    return () => window.clearInterval(timer);
  }, []);
}
